import type { MessageObject, MusicMessageObject } from '@/types'

// Ответ GET /user. Поля опциональны: состав зависит от редакции (b2c/b2b) и способа входа.
export interface User {
	id?: string
	msisdn?: number
	email?: string
	plan?: string
	onboarding_complete?: boolean
}

// ---- Реестр задач генерации (Phase 5 · Step 2) ----
export type GenType = 'image' | 'image_edit' | 'music' | 'song'
export type GenStatus = 'creating' | 'pending' | 'success' | 'failed' | 'cancelled' | 'copyright'

export interface GenerationTask {
	threadId: number
	type: GenType
	taskId: string | null
	status: GenStatus
	prompt: string
	meta?: { style?: string; vocal_gender?: 'm' | 'f'; imageHash?: string }
}

export type PersistedTask = Pick<GenerationTask, 'threadId' | 'type' | 'taskId' | 'status' | 'prompt' | 'meta'>

export type CreateThreadData =
	| {
			promt: string
			topic_id?: number
			name?: string
			type?: string
			model_name?: string
			is_secret?: boolean
			is_reasoning?: boolean
	  }
	| {
			topic_id: number
			promt?: string
			name?: string
			type?: string
			model_name?: string
			is_secret?: boolean
			is_reasoning?: boolean
	  }

export type CreateThreadDataWithModel = CreateThreadData & {
	model_name: string
}

export type CreateMessageData = {
	thread_id: number
	document?: File
	image?: File
	promt: string
	prompt: string
	image_hash?: string
	is_reasoning?: boolean
}

export type CreateMessageDataWithModel = CreateMessageData & {
	model_name: string
}

export interface transcribeAudioData {
	audio: Blob
	topic_id?: number
	thread_id?: number
}

export interface cancelThreadStreamData {
	request_id: number
}

export interface CreateMusicGenerationData {
	thread_id: number
	model_name: string
	prompt: string
}

export interface CreateSongGenerationData {
	thread_id: number
	model_name: string
	prompt: string
	style: string
	vocal_gender: 'm' | 'f'
}

export interface SongGenerationResponse {
	task_id: string
	status: 'pending'
	userMessage?: MessageObject | null
	aiMessage?: MusicMessageObject | null
	error?: {
		message: string
		status: number
		code: string
	}
}

export interface SongStatusResponse {
	task_id: string
	status: 'pending' | 'success' | 'failed'
	userMessage: MessageObject | null
	aiMessage: MusicMessageObject | null
	error?: {
		message: string
		status: number
		code: string
	}
}

export interface MusicGenerationResponse {
	task_id: string
	status: 'pending'
	userMessage?: MessageObject | null
	aiMessage?: MusicMessageObject | null
	error?: {
		message: string
		status: number
		code: string
	}
}

export interface MusicStatusResponse {
	task_id: string
	status: 'pending' | 'success' | 'failed'
	userMessage: MessageObject | null
	aiMessage: MusicMessageObject | null
	error?: {
		message: string
		status: number
		code: string
	}
}

// ---- Биллинг (BILLING_V1_SPEC.md §3, Приложение А) ----

/**
 * Статус заказа в наших терминах. На проводе их три (pending/success/fail), перевод —
 * в endpoints/billing.ts. Значение expired фронт ставит себе сам, когда вышел наш
 * дедлайн поллинга: бэк такого статуса не присылает.
 *
 * Важно: из статуса больше НЕ следует, была ли попытка списания. Свежий заказ и заказ
 * с уже ушедшими деньгами оба приходят как pending, поэтому запирать генерации по
 * статусу нельзя — факт попытки фронт знает только от платёжного виджета
 * (см. chargeAttempted в stores/billing.ts).
 */
export type OrderStatus = 'pending' | 'succeeded' | 'failed' | 'expired'

/** Статусы заказа на проводе. */
export type WireOrderStatus = 'pending' | 'success' | 'fail'

/**
 * Машиночитаемая причина отказа. Список открытый: неизвестный код не должен ломать
 * разбор — показываем общий текст, сам код пишем в лог.
 */
export type FailureReason =
	| 'card_declined'
	| 'insufficient_funds'
	| 'expired_card'
	| '3ds_failed'
	| 'cancelled_by_user'
	| 'provider_error'
	| 'timeout'

/** Ресурсы, которые тарифицируются поштучно. Зеркало UsageResource из stores/usage.ts:
 * тянуть стор в api-слой нельзя, а общий тип ради трёх ключей заводить незачем. */
export type PlanResource = 'text' | 'image' | 'song'

/**
 * Сырой тариф с провода (GET /billing/plans). Все поля необязательны: бэк выкатывает
 * контракт по частям, и недостающее поле не повод отказаться от плана целиком.
 */
export interface WirePlan {
	name?: string
	slug?: string
	/** Цена в копейках — минорных единицах валюты. */
	price?: number
	/** Срок действия тарифа в днях. */
	days_period?: number
	currency?: string
	limits?: Partial<Record<PlanResource, number>>
}

/**
 * Тариф в терминах фронта. Копейки переводятся в рубли ровно один раз — в toPlan
 * (endpoints/billing.ts), дальше по коду ходят обе величины, и никто не делит на 100
 * повторно. priceMinor нужен форматированию (дробная часть), priceMajor — ассерту суммы
 * виджета, который приходит в рублях.
 */
export interface Plan {
	name: string
	slug: string
	priceMinor: number
	priceMajor: number
	daysPeriod: number
	currency: string
	limits: Record<PlanResource, number>
}

/**
 * Параметры платёжного виджета. Намеренно непрозрачны: payload приходит нашему бэку
 * от внешнего сервиса и смешивает две генерации API виджета (publicId/publicTerminalId,
 * invoiceId/externalId). Фронт разворачивает его как есть — иначе он окажется третьей
 * стороной, обязанной следить за версией чужого виджета.
 *
 * amount описан явно только ради защитного ассерта (см. usePaymentFlow): фронт его
 * не пересчитывает, нормализация единиц целиком на бэке.
 */
export interface WidgetPayload {
	amount?: number
	/**
	 * Номер заказа, которым бэк ключует GET /billing/orders/{id}. Второе и последнее поле
	 * payload, которое фронт читает по имени: без него наблюдать заказ нечем. Тип двойной
	 * — идентификатор родом из внешнего сервиса, и что он всегда строка, нам никто не обещал.
	 */
	invoiceId?: string | number
	externalId?: string | number
	[key: string]: unknown
}

/**
 * Сырые ответы платёжных ручек — как они приходят с провода, до перевода статуса.
 * Живут только внутри endpoints/billing.ts, наружу оттуда уходят типы ниже.
 *
 * status описан широко (не WireOrderStatus): новое значение от бэка не должно ломать
 * разбор ответа, его обрабатывает toOrderStatus.
 */
/**
 * Ответ чекаута. Форм две, и обе живые.
 *
 * Была: конверт `{ order_id, status, expires_at, widget }`. Стала (dev, 2026-09-01): телом
 * ответа приходит сам payload виджета — ни номера заказа, ни статуса, ни дедлайна в нём нет.
 * Описываем обе разом, а сводит их к одной toCheckout: фронту от конверта ничего не нужно —
 * наблюдение идёт по widget.invoiceId, а свежий заказ по определению незавершён.
 */
export interface WireCheckoutResponse extends WidgetPayload {
	/**
	 * Собственный номер заказа бэка. Фронт его не читает: наблюдение идёт по
	 * widget.invoiceId. В новой форме ответа этого поля нет вовсе.
	 */
	order_id?: string
	status?: string
	/** Дедлайн заказа. Может не прийти — тогда поллер живёт по своему запасному TTL. */
	expires_at?: string | null
	/** Старая форма: payload лежал внутри. В новой payload — это всё тело ответа. */
	widget?: WidgetPayload
}

export interface WireOrderResponse {
	order_id: string
	status?: string
	failure_reason?: FailureReason | string | null
	expires_at?: string | null
}

/**
 * Ответ POST /billing/checkout в наших терминах.
 *
 * Номера заказа здесь нет намеренно: единственный id, которым опрашивается заказ, лежит
 * в widget.invoiceId (см. usePaymentFlow). Пока поле было в этом типе, поллинг по чужому
 * id оставался выразимым — теперь он невозможен.
 */
export interface CheckoutResponse {
	status: OrderStatus
	expires_at: string | null
	widget: WidgetPayload
}

/** Ответ GET /billing/orders/{order_id} в наших терминах. */
export interface OrderResponse {
	order_id: string
	status: OrderStatus
	failure_reason: FailureReason | string | null
	expires_at?: string | null
}

/** Ответ POST /billing/subscription/cancel. */
export interface CancelSubscriptionResponse {
	status?: string
	auto_renew?: boolean
	current_period_end?: string | null
}

/** Заказ в терминах фронта. Всё, что берём из контракта, — здесь и больше нигде. */
export interface Order {
	orderId: string
	status: OrderStatus
	failureReason: FailureReason | null
	/** Код, который бэк прислал, но мы не знаем: для лога, не для UI. */
	unknownFailureCode: string | null
	expiresAt: string | null
}

/** Коды ошибок платёжных ручек, на которые фронт реагирует по-особому. */
export const BILLING_ERROR_CODES = {
	/** Pro уже активен: не ошибка, а повод рефетчнуть /usage и закрыть пейволл. */
	SUBSCRIPTION_ALREADY_ACTIVE: 'SUBSCRIPTION_ALREADY_ACTIVE',
	/** Бэк не сделал идемпотентность и вернул живой заказ отдельным кодом. */
	ORDER_ALREADY_EXISTS: 'ORDER_ALREADY_EXISTS',
	/** Заказа нет или он чужой: стоп поллинга, чистка хранилища. */
	ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
	/** Автопродление уже выключено — для пользователя это успех, а не ошибка. */
	AUTO_RENEW_ALREADY_OFF: 'AUTO_RENEW_ALREADY_OFF',
	GUEST_NOT_ALLOWED: 'GUEST_NOT_ALLOWED',
	PLAN_NOT_FOUND: 'PLAN_NOT_FOUND',
	PAYMENT_PROVIDER_UNAVAILABLE: 'PAYMENT_PROVIDER_UNAVAILABLE'
} as const
